<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            isAdd
              ? `${$t("actions.add")} ${$tc("ai_analytics_servers.device", 1)}`
              : `${$t("actions.edit")} ${$tc("ai_analytics_servers.device", 1)}`
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>
      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <validation-provider
            #default="validationContext"
            name="fire_threshold"
            rules="required"
          >
            <b-form-group
              :label="$t('ai_analytics_servers.server')"
              label-for="id_analytic_server"
            >
              <v-select
                label="name"
                v-model="itemData.id_analytic_server"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="servers"
                :filterable="false"
                :loading="isLoadingServers"
                class="w-100"
                :reduce="(val) => val.id"
                append-to-body
                :clearable="true"
                :calculate-position="withPopper"
                @search="searchServers"
                @input="serverChange"
              >
                <template #list-footer>
                  <li class="pagination justify-content-center py-50 mb-0">
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      :disabled="isLoadingServers || !hasPreviousServerPage"
                      @mousedown.prevent
                      @click="previousServerPage"
                    >
                      <feather-icon icon="ChevronLeftIcon" size="18" />
                    </b-button>
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      class="ml-50"
                      :disabled="isLoadingServers || !hasNextServerPage"
                      @mousedown.prevent
                      @click="nextServerPage"
                    >
                      <feather-icon icon="ChevronRightIcon" size="18" />
                    </b-button>
                  </li>
                </template>
              </v-select>
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Camera -->
          <cameras-proxy-select
            v-model="itemData.camera"
            :label="$t('ai_analytics_servers.device')"
            name="camera"
            rules="required"
            :reduce="(val) => val.value"
            class="mb-1"
          />

          <template v-if="serverType === 'fire_smoke'">
            <!-- Fire Threshold -->
            <validation-provider
              #default="validationContext"
              name="fire_threshold"
              :rules="{ required: false, between: { min: 0, max: 1 } }"
            >
              <b-form-group
                :label="`${$t('ai_analytics_servers.fire_threshold')} ${itemData.fire_threshold !== null ? `(${Math.round(itemData.fire_threshold * 100)}%)` : ''}`"
                label-for="fire_threshold"
              >
                <b-form-input
                  id="fire_threshold"
                  v-model="itemData.fire_threshold"
                  type="range"
                  step="0.01"
                  min="0"
                  max="1"
                  :state="getValidationState(validationContext)"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Smoke Threshold -->
            <validation-provider
              #default="validationContext"
              name="smoke_threshold"
              :rules="{ required: false, between: { min: 0, max: 1 } }"
            >
              <b-form-group
                :label="`${$t('ai_analytics_servers.smoke_threshold')} ${itemData.smoke_threshold !== null ? `(${Math.round(itemData.smoke_threshold * 100)}%)` : ''}`"
                label-for="smoke_threshold"
              >
                <b-form-input
                  id="smoke_threshold"
                  v-model="itemData.smoke_threshold"
                  type="range"
                  step="0.01"
                  min="0"
                  max="1"
                  :state="getValidationState(validationContext)"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Use Local Confirmation -->
            <b-form-group
              :label="$t('ai_analytics_servers.use_local_confirmation')"
              label-for="use_local_confirmation"
            >
              <b-form-checkbox
                id="use_local_confirmation"
                v-model="itemData.use_local_confirmation"
                switch
              />
            </b-form-group>
          </template>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ isAdd ? $t("actions.add") : $t("actions.save") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInvalidFeedback,
  BButton,
  BFormInput,
  BFormCheckbox,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { computed, ref, watch } from "@vue/composition-api";
import { required } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import CamerasProxySelect from "@/components/CamerasProxySelect.vue";
import vSelect from "vue-select";
import popperHelper from "@/libs/popper-helper";
import { debounce } from "lodash";

export default {
  name: "AiAnalyticsDevicesSidenav",
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInvalidFeedback,
    BButton,
    BFormInput,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver,
    CamerasProxySelect,
    vSelect,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
      default: () => ({}),
    }
  },
  data() {
    return {
      required,
    };
  },
  setup(props, { emit }) {
    const toast = useToast();

    const blankData = {
      id: null,
      camera: null,
      fire_threshold: null,
      smoke_threshold: null,
      use_local_confirmation: false,
      id_analytic_server: null,
      old_id_analytic_sever: null
    };

    const serverType = ref(null);
    const serverOptions = ref([]);
    const selectedServer = ref(null);
    const serverPage = ref(1);
    const serverLastPage = ref(1);
    const serverSearch = ref('');
    const isLoadingServers = ref(false);
    let serverRequestId = 0;

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));

    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      serverType.value = null;
      selectedServer.value = null;
      serverOptions.value = [];
      serverPage.value = 1;
      serverLastPage.value = 1;
      serverSearch.value = '';
    };

    const servers = computed(() => {
      if (!selectedServer.value || serverOptions.value.some(server => server.id === selectedServer.value.id)) {
        return serverOptions.value;
      }

      return [selectedServer.value, ...serverOptions.value];
    });

    const applyServerType = (server) => {
      if (!server) {
        serverType.value = null;
        return;
      }

      if (server.server_type === 'fire_smoke') {
        itemData.value.fire_threshold = itemData.value.fire_threshold ?? .35;
        itemData.value.smoke_threshold = itemData.value.smoke_threshold ?? .40;
        itemData.value.use_local_confirmation = itemData.value.id
          ? itemData.value.use_local_confirmation
          : true;
      } else {
        itemData.value.fire_threshold = null;
        itemData.value.smoke_threshold = null;
        itemData.value.use_local_confirmation = false;
      }

      serverType.value = server.server_type;
    };

    const loadServers = async (page = 1, search = serverSearch.value) => {
      const requestId = ++serverRequestId;
      isLoadingServers.value = true;

      try {
        const response = await store.dispatch('ai_analytics_servers/servers/getPage', {
          q: search || undefined,
          max: 25,
          'page[n]': page,
          sortBy: 'name',
        });

        if (requestId !== serverRequestId) {
          return;
        }

        serverOptions.value = response.data || [];
        serverPage.value = response.meta?.current_page || page;
        serverLastPage.value = response.meta?.last_page || 1;
      } catch (error) {
        if (requestId === serverRequestId) {
          serverOptions.value = [];
          serverLastPage.value = 1;
        }
      } finally {
        if (requestId === serverRequestId) {
          isLoadingServers.value = false;
        }
      }
    };

    const loadSelectedServer = async (id) => {
      if (!id) {
        selectedServer.value = null;
        applyServerType(null);
        return;
      }

      const loadedServer = serverOptions.value.find(server => server.id === id);
      if (loadedServer) {
        selectedServer.value = loadedServer;
        applyServerType(loadedServer);
        return;
      }

      try {
        const response = await store.dispatch('ai_analytics_servers/servers/getById', id);
        selectedServer.value = response.data || null;
        applyServerType(selectedServer.value);
      } catch (error) {
        selectedServer.value = null;
        applyServerType(null);
      }
    };

    const searchServers = debounce((search) => {
      serverSearch.value = search;
      loadServers(1, search);
    }, 300);

    const hasPreviousServerPage = computed(() => serverPage.value > 1);
    const hasNextServerPage = computed(() => serverPage.value < serverLastPage.value);

    const nextServerPage = () => loadServers(serverPage.value + 1);
    const previousServerPage = () => loadServers(serverPage.value - 1);

    // Cargar datos cuando se edita
    watch(
      () => props.itemEdit,
      (newVal) => {
        if (newVal && Object.keys(newVal).length > 0 && !props.isAdd) {
          const config = newVal.config && typeof newVal.config === 'object'
            ? newVal.config
            : {};

          itemData.value = {
            id: newVal.id,
            camera: newVal.id_camera_proxy,
            id_analytic_server: newVal.id_analytic_server,
            old_id_analytic_sever: newVal.id_analytic_server,
            fire_threshold: newVal.fire_threshold ?? config.fire_threshold ?? null,
            smoke_threshold: newVal.smoke_threshold ?? config.smoke_threshold ?? null,
            use_local_confirmation: newVal.vlm_validation
              ?? config.vlm_validation
              ?? config.use_local_confirmation
              ?? false,
          };

          if (newVal.server) {
            selectedServer.value = newVal.server;
            applyServerType(newVal.server);
          }

          loadSelectedServer(newVal.id_analytic_server);
        }
      },
      { immediate: true, deep: true }
    );

    watch(
      () => props.isSidebarActive,
      (isActive) => {
        if (isActive) {
          loadServers();
        }
      },
      { immediate: true }
    );

    const onSubmit = async () => {
      const payload = {
          id_camera_proxy: itemData.value.camera,
          id_analytic_server: itemData.value.id_analytic_server,
          old_id_analytic_sever: itemData.value.old_id_analytic_sever,
          fire_threshold: itemData.value.fire_threshold ? parseFloat(itemData.value.fire_threshold) : null,
          smoke_threshold: itemData.value.smoke_threshold ? parseFloat(itemData.value.smoke_threshold) : null,
          use_local_confirmation: itemData.value.use_local_confirmation || false,
      };

      // En modo edición, agregar el status actual
      if (!props.isAdd) {
        payload.status = props.itemEdit.on_server_status;
      }

      const action = props.isAdd ? "add" : "updateStatus";
      store.dispatch(`ai_analytics_servers/devices/${action}`, payload)
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: String(response.message),
                icon: "CheckIcon",
                variant: "success",
              },
            });

            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: String(response.message),
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((error) => {
          const isLocalChange = error.response?.status === 400;

          toast({
            component: ToastificationContent,
            props: {
              title: String(error.response?.data?.message || "Error al procesar la solicitud"),
              icon: "AlertTriangleIcon",
              variant: isLocalChange ? "warning" : "danger",
            },
          });

          if (isLocalChange) {
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          }
        });
    };

    const serverChange = (idServer) => {
      const serverSelected = servers.value.find(server => server.id === idServer);
      selectedServer.value = serverSelected || null;
      applyServerType(serverSelected);
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetData);

    return {
      itemData,
      serverType,
      servers,
      isLoadingServers,
      hasPreviousServerPage,
      hasNextServerPage,
      searchServers,
      nextServerPage,
      previousServerPage,
      onSubmit,
      refFormObserver,
      getValidationState,
      resetForm,
      serverChange,
      withPopper: popperHelper.withPopper
    };
  },
};
</script>
<style scoped>
  body.dark-layout .custom-range {
    background-color: var(--third-dark) !important;
  }
</style>
